import { Core } from "@faire/design-tokens";
import { AccessibilityAnchor } from "@faire/retailer-visitor-shared/lib/AccessibilityLink";
import { shouldShowImprintPage } from "@faire/retailer-visitor-shared/lib/shouldShowImprintPage";
import { useSettingCookieConsentKey } from "@faire/retailer-visitor-shared/settings/useSettingCookieConsentKey";
import { useStrictLocalization } from "@faire/web/common/localization";
import { isNotNull } from "@faire/web/common/typescriptUtils";
import { Color } from "@faire/web/slate/Color";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import copyrightPagePath from "@faire/web-api--source/routes/www/copyright";
import accessibilityPagePath from "@faire/web-api--source/routes/www/aoda";
import imprintPagePath from "@faire/web-api--source/routes/www/imprint";
import privacyPagePath from "@faire/web-api--source/routes/www/privacy";
import cookiesPagePath from "@faire/web-api--source/routes/www/privacy/cookies";
import siteMapPagePath from "@faire/web-api--source/routes/www/site-map";
import termsOfServicePagePath from "@faire/web-api--source/routes/www/tos";
import React from "react";
import styled, { css } from "styled-components";

interface IProps {
  className?: string;
  inversed?: boolean;
  backgroundColor?: Color;
}

export const LegalLinks: React.FC<IProps> = ({
  className,
  inversed,
  backgroundColor,
}) => {
  const cookieConsentKey = useSettingCookieConsentKey();
  const links = useLinks(!!cookieConsentKey, shouldShowImprintPage());
  return (
    <Wrapper
      className={className}
      $backgroundColor={backgroundColor}
      $inversed={inversed}
    >
      <Typography color={Core.text.subdued} variant="paragraphSansRegular">
        ©{new Date().getFullYear()} Faire Wholesale, Inc.
      </Typography>
      <Spacer height="1x" />
      <WrappingRow>
        {links.map((item, index) => (
          <Row key={`${item.text}-${index}`}>
            <AccessibilityAnchor
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Typography color={Core.text.subdued}>{item.text}</Typography>
            </AccessibilityAnchor>
            {index + 1 === links.length ? null : ( // no separator for last link
              <SeparatorTypography> • </SeparatorTypography>
            )}
          </Row>
        ))}
      </WrappingRow>
    </Wrapper>
  );
};

const WrappingRow = styled(Row)`
  flex-wrap: wrap;
`;

const Wrapper = styled(Column)<{
  $backgroundColor?: Color;
  $inversed?: boolean;
}>`
  ${(props) =>
    props.$backgroundColor
      ? css`
          background-color: ${props.$backgroundColor ?? Core.surface.inverse};
          transition: background-color 500ms linear;
        `
      : ""}
  ${(props) =>
    props.$inversed
      ? css`
          p {
            color: ${Core.text.inverse};
          }
        `
      : ""}
`;

const SeparatorTypography = styled(Typography).attrs({
  color: "graphite",
})`
  &&& {
    padding: ${getSpacing(0, "1x")} !important;
  }
`;

export const useLinks = (
  includeCookies?: boolean,
  includeImprint?: boolean
): { link: string; text: string }[] => {
  const { strictLocalize } = useStrictLocalization();
  return [
    {
      link: termsOfServicePagePath(),
      text: strictLocalize({
        defaultMessage: "Terms of Service",
      }),
    },
    {
      link: privacyPagePath(),
      text: strictLocalize({
        defaultMessage: "Privacy Policy",
      }),
    },
    includeCookies
      ? {
          link: cookiesPagePath(),
          text: strictLocalize({
            defaultMessage: "Cookie Policy",
          }),
        }
      : null,
    {
      link: copyrightPagePath(),
      text: strictLocalize({
        defaultMessage: "IP Policy",
        description: {
          text: "A link to our legal policy for our intellectual property",
        },
      }),
    },
    {
      link: accessibilityPagePath(),
      text: strictLocalize({
        defaultMessage: "Accessibility Policy",
        description: {
          text: "A link to our legal policy for accessibility",
        },
      }),
    },
    {
      link: siteMapPagePath(),
      text: strictLocalize({
        defaultMessage: "Sitemap",
        description: {
          text: "A link to the sitemap page for the website",
        },
      }),
    },
    includeImprint
      ? {
          link: imprintPagePath(),
          text: strictLocalize({
            defaultMessage: "Imprint",
            description: {
              text: "Name of a page containing information about Faire",
              img: "https://cdn.faire.com/fastly/4fb49ada1dbe770a5f95b42ad6ea170b6f396db21112f558851a06b0450fcef0.png",
            },
          }),
        }
      : null,
  ].filter(isNotNull);
};
